/**
 * WHAT THE RELATIONSHIP WORKSPACE MAY OFFER AS A CANDIDATE (#2351).
 *
 * Pure functions over registry rows and governed reads, in the same shape as
 * `relations.ts` beside it and for the same reason: every rule here is a
 * statement about what the SERVER will accept, so it has to be checkable
 * without a database and without a browser.
 *
 * Four questions, and each one is answered from a server fact rather than from
 * a convention:
 *
 *   · how many may be selected      — the cardinality predicate
 *                                     `core.validate_relationship` enforces
 *   · which are already linked      — the CURRENT edges of this slot
 *   · which sorts may be offered    — what the reading branch can honour
 *   · whether "Active only" exists   — whether the target declares the concept
 *
 * The last two are the same rule twice: a control that cannot change the answer
 * must be ABSENT, not inert. A sort select with three options of which two do
 * nothing, or an "Active only" switch over an entity with no notion of active,
 * both tell the person something untrue about what they are looking at.
 */
import type { Row, SemanticField } from '@solum/sdk'
import type { RelationRow } from './useRelations'
import type { RelationSlot } from './relations'

/**
 * May this slot hold more than one target from THIS end?
 *
 * Derived from the server's own predicate rather than from a convention.
 * `core.validate_relationship` (core 0.242.0, `2026082211:327-353`) refuses a
 * second OVERLAPPING edge in exactly two shapes:
 *
 *   `many_to_one | one_to_one` — one overlapping target FROM the from-endpoint
 *   `one_to_many | one_to_one` — one overlapping source INTO the to-endpoint
 *
 * An `out` slot reads from the from-endpoint and an `in` slot from the
 * to-endpoint, so the two arms below are those two clauses read from this
 * record. `both` is only ever produced for a symmetric self-referential EDGE,
 * which is `many_to_many` in every registration in the tree and is unbounded
 * either way.
 *
 * ── why not `relationAllowsMultiple` ──────────────────────────────────────
 * `packages/ui/src/controls/Form.tsx:69` already answers a nearly identical
 * question and is DIRECTION-BLIND: it treats `one_to_many` as many and
 * `many_to_one` as one, which is correct for the from-end of an fk `ref` FIELD,
 * the only thing it is used for. Handed an inbound slot it inverts the answer —
 * an organisation's "Subsidiary of" (`parent_of`, read `in`) would offer
 * multi-select for a parent the database permits exactly one of. Widening that
 * helper to take a direction would put a relationship concept inside a form
 * field's contract; this is the same rule stated where slots live.
 */
export function slotAdmitsMany(slot: Pick<RelationSlot, 'direction' | 'cardinality'>): boolean {
  if (slot.direction === 'out') return slot.cardinality === 'one_to_many' || slot.cardinality === 'many_to_many'
  if (slot.direction === 'in') return slot.cardinality === 'many_to_one' || slot.cardinality === 'many_to_many'
  return true
}

/** The selection cap: 1, or null for "as many as you like". */
export function slotMaxSelection(slot: Pick<RelationSlot, 'direction' | 'cardinality'>): number | null {
  return slotAdmitsMany(slot) ? null : 1
}

/**
 * The endpoint ids this record is ALREADY linked to through this slot.
 *
 * "Already" means an edge that OVERLAPS today, because that is what the
 * database refuses a second of: an ended `works_at` is a historical fact and
 * re-hiring somebody is a legitimate new edge, so treating it as already-linked
 * would disable the one row the person came to add. An FK-backed slot has no
 * edge row at all (`RelationRow.edge` is null) and every row it returns is a
 * current link by construction.
 *
 * `today` is passed in rather than read, so the boundary is testable on the day
 * an edge ends.
 */
export function linkedEndpointIds(rows: readonly RelationRow[], today: string): Set<string> {
  const linked = new Set<string>()
  for (const { record, edge } of rows) {
    const id = String(record?.id ?? '')
    if (!id) continue
    if (!edge) { linked.add(id); continue }
    const endsOn = edge.valid_to == null ? null : String(edge.valid_to)
    if (endsOn === null || endsOn >= today) linked.add(id)
  }
  return linked
}

/* ── the reading branch, and what it can honestly offer ─────────────────── */

/**
 * `door` — the target entity has a registered `search_*` door: ranked,
 * whole-table, under the caller's own RLS, and the only thing it takes is a
 * query and a limit.
 * `table` — no door, so the entity's own table is read server-side with an
 * `ilike` over its declared text columns, which CAN page and CAN order.
 * `none` — neither is available (an unregistered entity), which is an explicit
 * state and not an empty list.
 */
export type CandidateBranch = 'door' | 'table' | 'none'

export type CandidateSort = 'best' | 'name' | 'recent'

export const CANDIDATE_SORT_LABELS: Record<CandidateSort, string> = {
  best: 'Best match',
  name: 'Name',
  recent: 'Recently updated',
}

/** The one column name this platform uses for an entity's own active concept. */
export const ACTIVE_COLUMN = 'is_active'

/** The columns a name sort and a recency sort can be built from. */
const RECENCY_COLUMNS = ['updated_at', 'created_at'] as const

const declared = (fields: readonly SemanticField[], column: string): SemanticField | undefined =>
  fields.find((field) => field.column_name === column)

/** The first declared text column — what a name sort orders by, when there is one. */
export function nameColumn(fields: readonly SemanticField[]): string | null {
  const text = fields.filter((field) => field.kind === 'text').sort((a, b) => a.sort - b.sort)
  return text[0]?.column_name ?? null
}

export function recencyColumn(fields: readonly SemanticField[]): string | null {
  for (const column of RECENCY_COLUMNS) if (declared(fields, column)) return column
  return null
}

/**
 * The sorts this branch can actually honour.
 *
 * A registered door ranks its own rows (`core.text_rank` since core 0083) and
 * takes no order argument, so `best` is the only truthful option and the
 * control has nothing to switch between. The table branch has no rank — an
 * `ilike` is not a ranking — so it offers exactly the orderings its declared
 * columns support.
 */
export function candidateSorts(branch: CandidateBranch, fields: readonly SemanticField[]): CandidateSort[] {
  if (branch === 'door') return ['best']
  if (branch === 'none') return []
  const sorts: CandidateSort[] = []
  if (nameColumn(fields)) sorts.push('name')
  if (recencyColumn(fields)) sorts.push('recent')
  return sorts
}

/**
 * Is an "Active only" filter offered at all?
 *
 * Only where the TARGET declares the concept — a described boolean
 * `is_active` — and only on the branch that can filter on it. A registered
 * search door takes a query and a limit and nothing else, so on that branch the
 * control is absent rather than present-and-ignored.
 *
 * Measured before choosing the column (rule 26 — lint for the property, not the
 * token): 32 `is_active boolean` declarations across the tree against a single
 * `active boolean`, and that one is a reference vocabulary rather than a
 * registered entity. `core_person` and `core_address` declare NEITHER, so on
 * the two relationship types in #2351's own mockups this control is correctly
 * absent, and the mockup's "Active only" chip is aspirational for them.
 */
export function activeFilterAvailable(branch: CandidateBranch, fields: readonly SemanticField[]): boolean {
  if (branch !== 'table') return false
  const field = declared(fields, ACTIVE_COLUMN)
  return field != null && field.kind === 'boolean'
}

/** Is THIS candidate row inactive, as its own entity defines it? */
export function candidateInactive(row: Row, fields: readonly SemanticField[]): boolean {
  const field = declared(fields, ACTIVE_COLUMN)
  if (!field || field.kind !== 'boolean') return false
  return row[ACTIVE_COLUMN] === false
}

/**
 * The PostgREST order clause for a sort, or undefined where the branch has none.
 *
 * A SUBJECT target (#2444 slice 4) is read through a door that orders one field
 * with Postgres's own blank placement — last ascending, FIRST descending — and
 * refuses a request that asks otherwise. So on that branch the clause asks for
 * exactly what the door does (no null modifier): an undated record sorts ahead
 * in "recent", which is what the list will then honestly show.
 */
export function candidateOrder(
  sort: CandidateSort, fields: readonly SemanticField[], subject = false,
): string | undefined {
  if (sort === 'name') {
    const column = nameColumn(fields)
    return column ? `${column}.asc${subject ? '' : '.nullslast'}` : undefined
  }
  if (sort === 'recent') {
    const column = recencyColumn(fields)
    return column ? `${column}.desc${subject ? '' : '.nullslast'}` : undefined
  }
  return undefined
}

/* ── the candidate row a surface draws ─────────────────────────────────────
 *
 * `alreadyLinked` DISABLES the row and `inactive` does not, which is the
 * mockup's own distinction and the right one: an existing edge is a fact the
 * database will refuse a duplicate of, while an inactive record is a judgement
 * the person is entitled to make. Both are stated in words on the row, because
 * a state carried only by colour is not a state for everybody.
 */
export interface RelationCandidate {
  id: string
  primary: string
  secondary: string | null
  alreadyLinked: boolean
  inactive: boolean
}

export interface CandidateWindow {
  items: RelationCandidate[]
  /**
   * More matches exist beyond this window. Derived from asking for one row more
   * than is shown, which is the only way the door branch can know: it reports
   * no total, and an exactly-full page is indistinguishable from a complete one
   * without the probe.
   */
  more: boolean
  /**
   * The server's total for this filter, or NULL where the branch cannot report
   * one. Null is "unavailable", which a surface must never draw as a count —
   * the rule `useRelatedCounts` states for its own absent slots.
   */
  total: number | null
}

/**
 * Shape a page of candidate rows.
 *
 * `hideLinked` is the DEFAULT and removes already-linked rows entirely; with it
 * off they stay visible, disabled and labelled, because "you already have this
 * one" is the answer to a question the person is plainly asking. Nothing here
 * filters by authority: the reads are the caller's own, so a row that arrived
 * is a row this caller may see, and a row that did not never becomes an
 * unselectable placeholder that discloses its existence.
 */
export function toCandidateWindow(
  rows: readonly Row[],
  {
    fields, linked, hideLinked, window, total, label,
  }: {
    fields: readonly SemanticField[]
    linked: ReadonlySet<string>
    hideLinked: boolean
    window: number
    total: number | null
    /** How a row is named — the caller's governed resolution, never an id. */
    label: (row: Row) => { primary: string; secondary: string | null }
  },
): CandidateWindow {
  const page = rows.slice(0, window)
  const items: RelationCandidate[] = []
  for (const row of page) {
    const id = String(row.id ?? '')
    if (!id) continue
    const alreadyLinked = linked.has(id)
    if (alreadyLinked && hideLinked) continue
    const { primary, secondary } = label(row)
    items.push({ id, primary, secondary, alreadyLinked, inactive: candidateInactive(row, fields) })
  }
  return { items, more: rows.length > window, total }
}
